import * as React from 'react';

import styled from 'styled-components';
import { Button, Text } from '@audi/audi-ui-react-v2';
import { useI18n } from '@oneaudi/i18n-context';
import { FeatureAppLoader } from '@feature-hub/react';
import { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import {
  getActualTestCycles,
  isPHEV,
  VehicleBasic,
  useConsumptionLabels,
  useServicesContext,
  FootnoteReference,
} from '@oneaudi/vtp-shared';

import { HighlightWrap, CommonHighlight } from './CommonStyledComponents';
import { HighLightItem } from '../interface/highlights.interfaces';
import HighlightInfo from './HighlightInfo';
import { messages } from '../i18n/messages';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { trackLayerLinkClick } from '../tracking/tracking';
import { EmissionRepresentationFromConfig } from '@oneaudi/vtp-configuration-service';

interface ConsumptionIconProps {
  item: HighLightItem;
  vehicle: VehicleBasic;
}

const ConsumptionValue = styled.span`
  display: block;
`;

const ConsumptionButton = styled(({ className, ...props }) => (
  <Button {...props} className={className} />
))`
  display: block;
`;
const LayerContainer = styled.div``;

const ConsumptionItem: React.FC<ConsumptionIconProps> = ({ item, vehicle }) => {
  const { IconLabel: ItemLabel, InfoLink, TechdataKey } = item;
  const [primaryConsumptionLabel, secondaryConsumptionLabel] = useConsumptionLabels(vehicle);
  const consumptionLink = useI18n(messages.consumptionLinkText);
  const consumptionCombinedLabel = useI18n(messages.consumptionCombinedLabel);
  const carType = vehicle.type === 'N' ? 'new' : 'used';
  const trackingService = useServicesContext().getAdditionalService(
    'audi-tracking-service',
  ) as TrackingServiceV2;

  const layerManager = useServicesContext().getAdditionalService(
    'gfa:layer-manager',
  ) as GfaLayerManagerV1;
  const config = useServicesContext().getConfiguration()!;
  const emissionRepresentation = config?.consumptionEmission
    ?.consumptionEmissionRepresentation as EmissionRepresentationFromConfig[];
  const actualTestCycles = getActualTestCycles(vehicle, emissionRepresentation) as Array<string>;
  const isMultiFuel: boolean = isPHEV(vehicle);
  const hasMultipleTestCycle = actualTestCycles?.length === 2;
  const consumptionVersion = config.featureApps?.consumptionVersion || '2.15.0';
  const scStartPageUrl = config.urls?.scStartPageLink;
  const openLayer = () => {
    layerManager?.openFocusLayer(
      () => (
        <LayerContainer className="highlightsLayer">
          <div data-fefa-custom-id="highlightsConsumption">
            <FeatureAppLoader
              featureAppId="highlightsConsumption"
              src={`https://fa-vtp-consumption.cdn.prod.arcade.apps.one.audi/${consumptionVersion}/fh/app.js`}
              config={{
                vehicleId: vehicle.id,
                eecImageUrl: `https://fa-vtp-consumption.cdn.prod.arcade.apps.one.audi/${consumptionVersion}/static`,
              }}
            />
          </div>
          <FeatureAppLoader
            featureAppId="highlights-footnotes"
            src="https://fa-footnote-engine.cdn.prod.arcade.apps.one.audi/v2.2.3/fh/app.js"
            config={{
              apiUrls: [
                `${scStartPageUrl}/commons/sc_footnotes/_jcr_content/content/sc_footnotes.json`,
              ],
              omitSideSpacing: true,
              omitBottomSpacing: true,
              uiTheme: 'light',
              layerElementClassName: 'highlightsLayer',
            }}
          />
        </LayerContainer>
      ),
      {},
      {
        size: FocusLayerSizeV2.A,
        userCloseable: true,
      },
    );

    trackLayerLinkClick(trackingService, consumptionLink);
  };

  return (
    <>
      {!!primaryConsumptionLabel && actualTestCycles?.length > 0 && (
        <>
          <HighlightWrap className="sc-detail-highlight-wrapper" data-tech-key={TechdataKey}>
            <CommonHighlight className="sc-detail-highlight" data-tech-key={TechdataKey}>
              <Text
                as="p"
                variant="copy2"
                spaceStackEnd="xxs"
                className="sc-detail-highlight-label"
              >
                {ItemLabel}
                <FootnoteReference refId={`fn_consumption_stockcar_${carType}`} />
              </Text>
              <Text as="span" variant="copy1" weight="bold">
                <ConsumptionValue>
                  {`${primaryConsumptionLabel} ${consumptionCombinedLabel}`}
                  <FootnoteReference refId={`fn_${actualTestCycles[0].toLowerCase()}_stockcar`} />
                  {isMultiFuel && hasMultipleTestCycle && (
                    <>
                      <sup>,</sup>
                      <FootnoteReference
                        refId={`fn_${actualTestCycles[1].toLowerCase()}_stockcar`}
                      />
                    </>
                  )}
                </ConsumptionValue>
                {!!secondaryConsumptionLabel && (
                  <ConsumptionValue>
                    {`${secondaryConsumptionLabel} ${consumptionCombinedLabel}`}
                    {isMultiFuel && hasMultipleTestCycle && (
                      <>
                        <FootnoteReference
                          refId={`fn_${actualTestCycles[0].toLowerCase()}_stockcar`}
                        />
                        <sup>,</sup>
                        <FootnoteReference
                          refId={`fn_${actualTestCycles[1].toLowerCase()}_stockcar`}
                        />
                      </>
                    )}
                    {!isMultiFuel && hasMultipleTestCycle ? (
                      <FootnoteReference
                        refId={`fn_${actualTestCycles[1].toLowerCase()}_stockcar`}
                      />
                    ) : (
                      <FootnoteReference
                        refId={`fn_${actualTestCycles[0].toLowerCase()}_stockcar`}
                      />
                    )}
                  </ConsumptionValue>
                )}
              </Text>
              {InfoLink ? <HighlightInfo infoLink={InfoLink} /> : ''}
              <ConsumptionButton
                variant="text"
                size="small"
                spaceStackStart="s"
                data-testid="consumptionLayer"
                onClick={openLayer}
              >
                {consumptionLink}
              </ConsumptionButton>
            </CommonHighlight>
          </HighlightWrap>
        </>
      )}
    </>
  );
};

export default React.memo(ConsumptionItem);
